import { useEffect, useState, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { AnimatePresence } from 'motion/react'
import { cva } from 'class-variance-authority'
import RichInput from '@/components/search/core/RichInput'
import Overlay from '@/components/search/common/Overlay'
import Spotlight from '@/components/search/core/Spotlight'
import Suggestion from '@/components/search/common/Suggestion'
import { cn } from '@/lib/utils'
import { useHeaderContext } from '@/context/HeaderContext'
import { useChatContext } from '@/context/ChatContext'
import { getActiveInput } from '@/lib/activeInput'

const container = cva(
  'absolute cursor-pointer top-0 z-40 min-h-10 h-fit navigation:min-h-8 right-0 transition-all rounded-[20px] ease-in-out overflow-hidden duration-300 border',
  {
    variants: {
      theme: {
        dark: 'bg-white/10 border-white/15 hover:bg-white/15 hover:border-white/20',
        light:
          'bg-gray-50 border-gray-200 hover:border-gray-300 dark:bg-white/10 dark:border-white/15 dark:hover:bg-white/15 dark:text-white dark:hover:border-white/20',
      },
      state: {
        open: 'dark:text-white text-black -translate-y-3 bg-white border-gray-100 hover:bg-white hover:border-gray-100 dark:bg-black dark:border-white/10 dark:hover:bg-black dark:hover:border-white/10 md:translate-x-0 xl:translate-x-[95px] navigation:w-[420px]',
        closed: 'w-full navigation:w-44',
      },
      scrolled: {
        true: '',
        false: 'w-10',
      },
    },
  },
)

const input = cva('flex w-full items-center gap-2 transition-all duration-300 ease-in-out', {
  variants: {
    state: {
      open: 'h-[54px] pl-2.5 pr-5',
      closed: 'h-[38px] navigation:h-[30px] px-2.5',
    },
    scrolled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      state: 'closed',
      scrolled: false,
      className: 'px-3 navigation:px-2.5',
    },
  ],
})

export default function DesktopSearch({ scrolled }: { scrolled?: boolean }) {
  const pathname = usePathname()
  const isSearchPage = pathname?.startsWith('/search/')
  const containerRef = useRef<HTMLDivElement>(null)

  const { theme } = useHeaderContext()
  const { showSearch, showSpotlight, openSearch, closeSearch, handleKeyTrigger, showSuggestion } =
    useChatContext()

  const [dynamicStyles, setDynamicStyles] = useState<{ width?: string; transform?: string }>({})

  const state = showSearch ? 'open' : 'closed'

  useEffect(() => {
    const calculateDynamicStyles = () => {
      const windowWidth = window.innerWidth
      const isAbove960 = windowWidth >= 960

      if (!showSearch || isAbove960 || !containerRef.current) {
        setDynamicStyles({})
        return
      }

      const parent = containerRef.current.parentElement
      if (!parent) return

      const parentRect = parent.getBoundingClientRect()
      const desiredWidth = windowWidth - 10
      const parentRightEdge = parentRect.right
      const desiredRightEdge = windowWidth - 5
      const translateX = desiredRightEdge - parentRightEdge

      setDynamicStyles({
        width: `${desiredWidth}px`,
        transform: `translateX(${translateX}px) translateY(-8px)`,
      })
    }

    calculateDynamicStyles()

    window.addEventListener('resize', calculateDynamicStyles)
    return () => window.removeEventListener('resize', calculateDynamicStyles)
  }, [showSearch])

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      // Skip "/" hotkey on search pages to avoid conflict with chat input
      if (isSearchPage && (event.key === '/' || event.code === 'Slash') && !event.shiftKey) {
        return
      }
      if (getActiveInput() === 'nav') {
        handleKeyTrigger(event)
      }
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [handleKeyTrigger, isSearchPage])

  return (
    <div className="relative h-10 w-full navigation:h-8 navigation:w-44">
      <div
        ref={containerRef}
        className={cn(container({ theme, state, scrolled }))}
        style={showSearch ? dynamicStyles : undefined}
      >
        <div className={cn(input({ state, scrolled }))} onClick={() => openSearch('nav')}>
          <RichInput condensed={true} visible={showSearch} hideIndicator={isSearchPage} />
        </div>
        {showSpotlight && <Spotlight type="nav" />}
      </div>

      <AnimatePresence>
        {showSearch && <Overlay close={() => closeSearch(true)} background={true} />}
      </AnimatePresence>

      <AnimatePresence>{showSuggestion && <Suggestion />}</AnimatePresence>
    </div>
  )
}
